// Backend origin resolver.
//
// The SPA normally derives every backend URL from `window.location`, which is
// exactly right when it is served from the Cloudflare Worker at
// eyes.wagmorelabs.com. Inside a Tauri app the page is loaded from
// `tauri://localhost` / `http://tauri.localhost`, so there is no useful origin
// to derive from — the app must talk to the remote prod backend explicitly.
//
// `EYES_PUBLIC_BACKEND_ORIGIN` is inlined at build time by Bun (`--env`, see
// client/build.ts). The web build leaves it `""` (empty → fall back to
// `window.location.origin`, byte-for-byte the old behavior). The Tauri build
// sets it to `https://eyes.wagmorelabs.com`. build.ts always sets the variable,
// so the reference is always replaced by a literal — no `process` at runtime.

const INJECTED = process.env.EYES_PUBLIC_BACKEND_ORIGIN;
const INJECTED_ORIGIN: string = typeof INJECTED === "string" ? INJECTED : "";

/** True when running inside a Tauri webview. */
export function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/**
 * The origin all backend requests are made against. Returns the injected
 * absolute origin (Tauri builds) or the current page origin (web builds),
 * never with a trailing slash.
 */
export function serverOrigin(): string {
  if (INJECTED_ORIGIN) return INJECTED_ORIGIN.replace(/\/+$/, "");
  return window.location.origin;
}

/** Absolute URL for an API path such as `/api/new`. */
export function apiUrl(path: string): string {
  return serverOrigin() + (path.startsWith("/") ? path : "/" + path);
}

/** WebSocket URL for a board, with the origin's scheme mapped to ws/wss. */
export function wsUrl(boardId: string): string {
  const origin = serverOrigin();
  const scheme = origin.startsWith("https:") ? "wss:" : "ws:";
  const host = origin.replace(/^https?:/, "");
  return `${scheme}${host}/api/${boardId}/ws`;
}

/** MCP JSON-RPC endpoint URL for a board. */
export function mcpUrl(boardId: string): string {
  return `${serverOrigin()}/MCP/${boardId}`;
}

/**
 * Public, shareable URL for a board. Always points at the canonical web origin
 * so a link copied from the desktop/mobile app opens in any browser — never a
 * `tauri://` URL that only resolves inside the app.
 */
export function boardShareUrl(boardId: string): string {
  return `${serverOrigin()}/${boardId}`;
}
